import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { FaArrowLeft, FaCheckCircle, FaTimesCircle, FaClock, FaMoneyBillAlt, FaCopy, FaCheck } from 'react-icons/fa';
import { FaAngleLeft } from 'react-icons/fa6';
import { formatLabel, getTransactionDetails } from '../../../actions/payments';
import { BsSendArrowUp, BsSendArrowDown } from "react-icons/bs";
import { SiConvertio } from 'react-icons/si';
import { useDispatch } from 'react-redux';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import './transactions.css';
import Logo from '../../../assets/img/Cardplot-dark.png';

const typeIcons = {
    credit: <BsSendArrowUp size={24} />,
    swap: <SiConvertio size={24} />,
    debit: <BsSendArrowDown size={24} />,
};

const TransactionDetails = () => {
    const { refId } = useParams();
    const navigate = useNavigate();
    const [transaction, setTransaction] = useState(null);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const [transactionParties, setTransactionParties] = useState(null);

    const getUserTransactions = async () => {
        try {
            setLoading(true);
            const response = await getTransactionDetails(refId);  
            const userTxn = response.data.transaction;
            if (!userTxn) {
                navigate(-1);
                return;
            }
            setTransaction(userTxn);
            setTransactionParties({
                sender: {
                    accountName: userTxn.bank_transfer_details?.sender_account_name || 
                        userTxn.metadata?.sender_details?.name || 
                        userTxn.metadata?.sender_details?.full_name || 
                        userTxn?.user?.full_name || 'Not Available',
                    accountNumber: userTxn.bank_transfer_details?.sender_account_number ||
                        userTxn.metadata?.sender_details?.email ||
                        userTxn?.user?.phone || userTxn?.user?.email,
                    bankName: userTxn.bank_transfer_details?.sender_bank_name || 'Cardplot',
                },
                recipient: {
                    accountName: userTxn.bank_transfer_details?.account_name || 
                        userTxn.metadata?.recipient_details?.name || 
                        userTxn.metadata?.recipient_details?.full_name || 
                        userTxn?.user?.full_name || 'Not Available',
                    accountNumber: userTxn.bank_transfer_details?.account_number ||
                        userTxn.metadata?.recipient_details?.email ||
                        userTxn?.user?.phone || userTxn?.user?.email,
                    bankName: userTxn.bank_transfer_details?.bank_name || 'Cardplot',
                },
            })
        } catch (error) {
            // console.error("Error fetching transactions:", error);
        } finally {
            setLoading(false);
        }
    }  
  
    useEffect(() => {
        if (refId) getUserTransactions();
    }, [refId]);

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (loading || !transaction) return (
        <div className="text-center mt-5 py-5">
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );

    const formatAmount = (amount, currencyCode) =>
        new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: currencyCode || transaction.wallet?.currency_code || 'NGN',
    }).format(parseFloat(amount));

    const statusConfig = {
        outstanding: { icon: <FaCheckCircle />, class: 'status-completed', text: 'Successful' },
        completed: { icon: <FaCheckCircle />, class: 'status-completed', text: 'Successful' },
        pending: { icon: <FaClock />, class: 'status-pending', text: 'Pending' },
        failed: { icon: <FaTimesCircle />, class: 'status-failed', text: 'Failed' },
    };

    const currentStatus = statusConfig[transaction.status] || statusConfig.pending;

    const handleDownloadReceipt = async () => {
        const txnId = transaction.reference || transaction.id || refId;
        const originalTitle = document.title;
        const fileName = `cardplot-receipt-${txnId}.pdf`;
        const receiptCard = document.querySelector('.receipt-card-modern');

        if (!receiptCard) {
            document.title = `cardplot-receipt-${txnId}`;
            window.print();
            setTimeout(() => {
                document.title = originalTitle;
            }, 1000);
            return;
        }

        setIsDownloading(true);

        try {
            const canvas = await html2canvas(receiptCard, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#FFFFFF',
                logging: false,
                ignoreElements: (element) => element.classList?.contains('no-print'),
                onclone: (clonedDoc) => {
                    const banner = clonedDoc.querySelector('.receipt-footer-banner');
                    if (banner) {
                        banner.classList.remove('d-none');
                        banner.style.display = 'block';
                    }
                    const footerText = clonedDoc.querySelector('.receipt-footer-text');
                    if (footerText) {
                        footerText.classList.remove('d-none');
                        footerText.style.display = 'block';
                    }
                }
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
            });

            const pageWidth = 210;
            const pageHeight = 297;
            const margin = 20;
            let imgWidth = pageWidth - (margin * 2);
            let imgHeight = (canvas.height * imgWidth) / canvas.width;

            const maxHeight = pageHeight - (margin * 2);
            if (imgHeight > maxHeight) {
                const ratio = maxHeight / imgHeight;
                imgHeight = maxHeight;
                imgWidth = imgWidth * ratio;
            }

            const x = (pageWidth - imgWidth) / 2;
            const y = (pageHeight - imgHeight) / 2;

            pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
            pdf.save(fileName);
        } catch (error) {
            console.error('Error generating receipt PDF, falling back to print:', error);
            document.title = `cardplot-receipt-${txnId}`;
            window.print();
            setTimeout(() => {
                document.title = originalTitle;
            }, 1000);
        } finally {
            setIsDownloading(false);
        }
    };

    const txnPurpose = transaction.purpose?.replace('_', ' ') || transaction.type;
    const txnIsInternational = transaction.type == 'debit' && transaction.metadata?.recepient_currency != transaction.currency;

  return (
    <section className='main transactions__page'>
        <div className="container" >
            <div className="mb-4 no-print">
                <button onClick={() => navigate(-1)} className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2" style={{ color: '#64748B' }}>
                    <FaAngleLeft size={18} />
                    <span className="fw-medium">Back to Transactions</span>
                </button>
            </div>

            <main>
                <div className="receipt-card-modern">
                    <div className="receipt-top-bar"></div>
                    
                    <div className="receipt-header-modern d-flex justify-content-between align-items-center mb-4">
                        <div className="receipt-logo-modern">
                            <img src={Logo} alt="Cardplot" height="28" />
                        </div>
                        <div className="receipt-title-modern fw-bold text-muted" style={{ fontSize: '12px', letterSpacing: '1px' }}>PAYMENT RECEIPT</div>
                    </div>

                    <div className="receipt-amount-section text-center mb-4">
                        <div className="receipt-amount-label text-muted fw-bold mb-2" style={{ fontSize: '11px', letterSpacing: '1px' }}>AMOUNT PAID</div>
                        <div className="receipt-amount-value fw-bold mb-3" style={{ fontSize: '32px', color: 'var(--txn-primary)' }}>
                            {formatAmount(transaction.amount)}
                        </div>
                        <div className="receipt-status-wrapper">
                            <span className="receipt-status-badge px-3 py-1 rounded" style={{ fontSize: '12px', backgroundColor: '#DCFCE7', color: '#10B981', fontWeight: '600' }}>
                                • {transaction.status == 'outstanding' && ['fee', 'statutory_fee'].includes(transaction.purpose) ? 'Completed' : (transaction.status === 'completed' || transaction.status === 'outstanding' ? 'Completed' : transaction.status)}
                            </span>
                        </div>
                    </div>

                    <div className="receipt-divider"></div>

                    <div className="receipt-details">
                            <div className="receipt-row d-flex flex-column flex-sm-row justify-content-between align-items-start gap-1 gap-sm-0">
                                <div className="receipt-label">Sender</div>
                                <div className="receipt-value text-start text-sm-end">
                                    <div className="fw-bold text-uppercase" style={{ fontSize: '13px' }}>
                                        {transactionParties.sender.accountName}
                                    </div>
                                    <div className="text-muted fw-normal" style={{ fontSize: '11px' }}>
                                        {transactionParties.sender.bankName} 
                                        {transactionParties.sender.accountNumber ? ` | ${transactionParties.sender.accountNumber}` : null}
                                    </div>
                                </div>
                            </div>

                            <div className="receipt-row d-flex flex-column flex-sm-row justify-content-between align-items-start gap-1 gap-sm-0">
                                <div className="receipt-label">Recipient</div>
                                <div className="receipt-value text-start text-sm-end">
                                    <div className="fw-bold text-uppercase" style={{ fontSize: '13px' }}>
                                        {transactionParties.recipient.accountName}
                                    </div>
                                    <div className="text-muted fw-normal" style={{ fontSize: '11px' }}>
                                        {transactionParties.recipient.bankName} 
                                        {transactionParties.recipient.accountNumber ? ` | ${transactionParties.recipient.accountNumber}` : null}
                                    </div>
                                </div>
                            </div>



                        <div className="receipt-row d-flex flex-column flex-sm-row justify-content-between align-items-start gap-1 gap-sm-0">
                            <div className="receipt-label">Transaction Type</div>
                            <div className="receipt-value fw-bold text-capitalize text-start text-sm-end" style={{ fontSize: '13px' }}>
                                { 
                                    transaction.purpose == 'fee' ? 'Transaction Fee' :
                                    transaction.purpose == 'statutory_fee' ? (transaction?.metadata?.statutory_fee_label || txnPurpose) :
                                    txnPurpose
                                }
                            </div>
                        </div>

                        {txnIsInternational && transaction.metadata?.recipient_amount != null && 
                        <div className="receipt-row align-items-center">
                            <span className="receipt-label">Amount Sent </span>
                            <span className="receipt-value fw-bold" style={{ fontSize: '13px' }}>
                                {formatAmount(transaction?.metadata?.recipient_amount, transaction.metadata.recepient_currency)}
                            </span>
                        </div>
                        }

                        <div className="receipt-row d-flex flex-column flex-sm-row justify-content-between align-items-start gap-1 gap-sm-0">
                            <div className="receipt-label">Status</div>
                            <div className="receipt-value text-start text-sm-end">
                                <span className="px-3 py-1 rounded text-capitalize fw-bold" style={{ fontSize: '11px', backgroundColor: '#DCFCE7', color: '#10B981' }}>
                                    {transaction.status == 'outstanding' && ['fee', 'statutory_fee'].includes(transaction.purpose) ? 'Completed' : (transaction.status === 'completed' || transaction.status === 'outstanding' ? 'Completed' : transaction.status)}
                                </span>
                            </div>
                        </div>

                        {transaction.metadata?.description && (
                            <div className="receipt-row d-flex flex-column flex-sm-row justify-content-between align-items-start gap-1 gap-sm-0">
                                <div className="receipt-label">Description</div>
                                <div className="receipt-value text-start text-sm-end fw-bold" style={{ fontSize: '13px' }}>{transaction.metadata.description}</div>
                            </div>
                        )}

                        <div className="receipt-row d-flex flex-column flex-sm-row justify-content-between align-items-start gap-1 gap-sm-0">
                            <div className="receipt-label">Date</div>
                            <div className="receipt-value fw-bold text-start text-sm-end" style={{ fontSize: '13px' }}>{new Date(transaction.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                        </div>

                        <div className="receipt-row d-flex flex-column flex-sm-row justify-content-between align-items-start gap-1 gap-sm-0">
                            <div className="receipt-label">Reference ID</div>
                            <div className="receipt-value fw-bold d-flex align-items-center justify-content-start justify-content-sm-end gap-2" style={{ fontSize: '13px' }}>
                                {transaction.reference}
                                <button className="copy-btn no-print p-0 m-0 bg-transparent border-0" onClick={() => handleCopy(transaction.reference)}>
                                    {copied ? <FaCheck size={12} color="#10B981" /> : <FaCopy size={12} color="#64748B" />}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="receipt-footer-banner d-none d-print-block mt-3 p-2 text-center mb-3" style={{ backgroundColor: '#F8F5FF', color: '#1E1B4B', fontSize: '12px', borderRadius: '8px' }}>
                        <div className="fw-bold">Not on Cardplot yet? Download the app</div>
                        <div style={{ fontSize: '11px', color: '#475569' }}>connecting &amp; moving Africa's money.</div>
                    </div>
                    
                    <div className="receipt-footer-text d-none d-print-block text-center text-muted" style={{ fontSize: '10px', lineHeight: '1.6', maxWidth: '400px', margin: '0 auto' }}>
                        <p className="mb-1">©️ {new Date().getFullYear()} Cardplot Technologies Limited. All rights reserved.</p>
                        <p className="mb-1">Registered address: 10 Toyin Street, Ikeja City, Lagos State, Nigeria.</p>
                        <p className="mb-1">Cardplot®️ is a registered trademark of Cardplot Technologies Limited RC: 8144330.</p>
                        <p className="mb-0">All transactions are processed and monitored through regulated financial institutions and licensed bank partners. Deposits are safeguarded in line with applicable regulatory requirements.</p>
                    </div>

                    <div className="mt-5 text-center no-print">
                        <button 
                            onClick={handleDownloadReceipt}
                            className="btn w-100 py-3 rounded-4 fw-bold d-flex align-items-center justify-content-center gap-2" 
                            style={{ 
                                background: 'var(--txn-primary)', 
                                color: '#FFFFFF', 
                                border: 'none', 
                                fontSize: '15px',
                                boxShadow: '0 4px 12px rgba(18, 27, 60, 0.15)',
                                cursor: isDownloading ? 'not-allowed' : 'pointer'
                            }}
                            disabled={isDownloading}
                        >
                            {isDownloading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    <span>Downloading Receipt...</span>
                                </>
                            ) : (
                                <span>Download Receipt</span>
                            )}
                        </button>
                    </div>
                </div>
            </main>
        </div>
    </section>
  );
};

export default TransactionDetails;
